/**
 * Product Hub — דרגות מחיר, the quantity ladder that sits between a shelf price and a line total.
 *
 * ## Why this is pricing and not a promotion
 *
 * `PROMOTION_TYPES` already contains `'quantity'` ("קנה 3 וקבל 20% הנחה"), so the distinction has
 * to be stated rather than assumed (ADR-019). A promotion is temporal — it has `startAt`/`endAt`,
 * a priority, a coupon, and it is evaluated over a cart. A price ladder is the pricelist itself:
 * permanent, dateless, shown on the pricelist screen next to the base price, and it is what the
 * store charges. ADR-004 stays intact — nothing here writes into `StorePrices`.
 *
 * ## Why `lineTotal` and not `unitPrice`
 *
 * The two tiering methods disagree, and the disagreement is the whole reason the file exists.
 * A ladder of `10 → 90`, `100 → 80` over a base of 100, for 120 units:
 *
 *  - **volume** — reaching a rung re-prices every unit:  `120 × 80 = 9,600`
 *  - **progressive** — only the units inside each band:  `9 × 100 + 90 × 90 + 21 × 80 = 10,680`
 *
 * Under `progressive` there is no single "price per unit", so the answer has to be a line total.
 * `unitPrice` comes back too, as `lineTotal / quantity`, but it is a **display value** — never
 * multiply by it, or the two numbers on screen will not add up.
 *
 * That is exactly what `effectiveUnitPrice` in `subscription.types.ts` cannot express, and it is
 * why that ladder is volume-only. The duplication between the two is deliberate; see ADR-019 for
 * the condition under which they merge.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through
 * `@malash-office/types/product-hub/price-ladder`.
 */
import { roundMoney } from './payment.types';
import { PhOption, phOptions } from './productHub.core';
import { toMoneyOrZero } from './productHub.money';

/* ------------------------------------------------------------------ */
/* Vocabulary                                                          */
/* ------------------------------------------------------------------ */

/**
 * What a rung says.
 *
 * `unit_price` fixes a price ("בהזמנת 10, מחיר ליחידה 90"); `percent` takes a share off whatever
 * the store already charges ("מעל 10 יחידות — 5% הנחה"). The second one is currency-free, which is
 * the reason a percent ladder may span stores in different currencies and an absolute one may not
 * (ADR-003: nothing in this module converts).
 */
export const PRICE_LADDER_MODES = ['unit_price', 'percent'] as const;
export type PriceLadderMode = (typeof PRICE_LADDER_MODES)[number];

export const PRICE_LADDER_MODE_LABELS: Record<PriceLadderMode, string> = {
  unit_price: 'מחיר קבוע ליחידה',
  percent: 'הנחה באחוזים'
};

export const PRICE_LADDER_MODE_OPTIONS: PhOption<PriceLadderMode>[] = phOptions(
  PRICE_LADDER_MODES,
  PRICE_LADDER_MODE_LABELS
);

/** How the rungs accumulate. The labels are the explanation, not a name — see the file header. */
export const PRICE_LADDER_METHODS = ['volume', 'progressive'] as const;
export type PriceLadderMethod = (typeof PRICE_LADDER_METHODS)[number];

export const PRICE_LADDER_METHOD_LABELS: Record<PriceLadderMethod, string> = {
  volume: 'כל הכמות לפי המדרגה שהושגה',
  progressive: 'רק היחידות בכל מדרגה'
};

export const PRICE_LADDER_METHOD_OPTIONS: PhOption<PriceLadderMethod>[] = phOptions(
  PRICE_LADDER_METHODS,
  PRICE_LADDER_METHOD_LABELS
);

/** Where the ladder a store uses came from. Specificity before priority, ADR-009. */
export const PRICE_LADDER_SOURCES = ['store_tier', 'default_tier', 'none'] as const;
export type PriceLadderSource = (typeof PRICE_LADDER_SOURCES)[number];

export const PRICE_LADDER_SOURCE_LABELS: Record<PriceLadderSource, string> = {
  store_tier: 'דרגות מחיר לחנות זו',
  default_tier: 'דרגות מחיר ברירת מחדל',
  none: 'ללא דרגות מחיר'
};

/**
 * One rung. Exactly one of `unitPrice` / `percentOff` is populated, decided by the ladder's `mode`
 * — the same one-column-per-type arrangement `VariantAttributeValues` uses.
 */
export interface PriceLadderRung {
  /** כמות מינימלית להפעלת המדרגה. Integer, 2 and up. */
  minQuantity: number;
  /** mode = 'unit_price': what one unit costs from this quantity up. */
  unitPrice?: number;
  /** mode = 'percent': the discount off the effective price, as a human number (5 = 5%). */
  percentOff?: number;
}

/** A ladder, without the storage columns around it. What every function here takes. */
export interface PriceLadder {
  mode: PriceLadderMode;
  method: PriceLadderMethod;
  tiers: readonly PriceLadderRung[];
}

/** Edited by hand in one dialog; a bound keeps the stored JSON small and readable. */
export const MAX_PRICE_LADDER_RUNGS = 10;

/* ------------------------------------------------------------------ */
/* Resolution                                                          */
/* ------------------------------------------------------------------ */

/** What a resolved ladder costs. `unitPrice` is for display only — see the file header. */
export interface LadderPrice {
  /** What the whole line costs. The number to trust. */
  lineTotal: number;
  /** `lineTotal / quantity`, rounded. Never multiply by this. */
  unitPrice: number;
  /** The rung that applied under `volume`, or the highest one cleared under `progressive`. */
  appliedMinQuantity: number | null;
}

/** The rungs sorted ascending, with the malformed ones dropped so a bad row cannot skew a total. */
const sortedRungs = (ladder: PriceLadder): PriceLadderRung[] =>
  [...ladder.tiers]
    .filter(rung => Number.isFinite(rung.minQuantity) && rung.minQuantity > 1)
    .sort((a, b) => a.minQuantity - b.minQuantity);

/**
 * What one unit costs on a given rung.
 *
 * A percent rung is resolved against `basePrice` — the store's **effective** price, not the
 * variant's central one. That mirrors the choice already documented on `applyBulkPriceOperation`
 * ("percentages work on the effective price"), so the two ways of asking "what does this cost
 * here?" cannot give two answers. Clamped at zero for the same reason it is there: a 120% discount
 * is a typo, and a negative price would propagate into every total downstream.
 */
const rungUnitPrice = (ladder: PriceLadder, rung: PriceLadderRung, basePrice: number): number =>
  ladder.mode === 'percent'
    ? Math.max(
        0,
        roundMoney(basePrice * (1 - (Number.isFinite(rung.percentOff ?? NaN) ? (rung.percentOff as number) : 0) / 100))
      )
    : Math.max(0, roundMoney(Number.isFinite(rung.unitPrice ?? NaN) ? (rung.unitPrice as number) : basePrice));

/**
 * The single decision point for "what does this quantity cost?".
 *
 * A null ladder, an empty one, or a quantity below the first rung all fall back to `basePrice ×
 * quantity`. That is not a special case — it is the default state of every variant in the
 * catalogue, and it has to cost the same as it did before any of this existed.
 */
export const resolveLadderPrice = (
  ladder: PriceLadder | null | undefined,
  basePrice: number,
  quantity: number
): LadderPrice => {
  const base = toMoneyOrZero(basePrice);
  const units = Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 0;

  const flat = (): LadderPrice => ({
    lineTotal: roundMoney(base * units),
    unitPrice: base,
    appliedMinQuantity: null
  });

  if (!ladder || units === 0) return flat();

  const rungs = sortedRungs(ladder);
  if (!rungs.length) return flat();

  // The highest rung the quantity clears. Null when it clears none — the base price still applies
  // to the whole line, which is what "בהזמנת 1 מחיר ליחידה 100" means.
  const reachedIndex = rungs.reduce((best, rung, index) => (units >= rung.minQuantity ? index : best), -1);
  if (reachedIndex === -1) return flat();

  if (ladder.method === 'volume') {
    const unitPrice = rungUnitPrice(ladder, rungs[reachedIndex], base);
    return {
      lineTotal: roundMoney(unitPrice * units),
      unitPrice,
      appliedMinQuantity: rungs[reachedIndex].minQuantity
    };
  }

  // Progressive: the units below the first rung cost the base price, and each band after it is
  // priced at its own rung. The bands are half-open — a rung at 10 starts *at* the 10th unit, so
  // 9 units precede it, which is what "מעל 10" means to the person who typed it.
  let remaining = units;
  let total = 0;
  let consumed = 0;

  const bandStart = rungs[0].minQuantity - 1;
  const baseUnits = Math.min(remaining, bandStart);
  total += base * baseUnits;
  remaining -= baseUnits;
  consumed += baseUnits;

  for (let index = 0; index <= reachedIndex && remaining > 0; index += 1) {
    const nextStart = index < reachedIndex ? rungs[index + 1].minQuantity - 1 : units;
    const bandUnits = Math.min(remaining, Math.max(0, nextStart - consumed));
    total += rungUnitPrice(ladder, rungs[index], base) * bandUnits;
    remaining -= bandUnits;
    consumed += bandUnits;
  }

  const lineTotal = roundMoney(total);
  return {
    lineTotal,
    unitPrice: roundMoney(lineTotal / units),
    appliedMinQuantity: rungs[reachedIndex].minQuantity
  };
};

/**
 * Pick the ladder a store uses — **specificity before priority** (ADR-009).
 *
 * A ladder attached to this store wins over the variant's default one, always. There is no numeric
 * tie-break because there is nothing to tie: `(variantId, storeId)` is unique on the attachment
 * table, so a store can reach at most one explicit ladder.
 */
export const resolveLadderForStore = <T extends { isDefault: boolean; storeIds: readonly number[] }>(
  ladders: readonly T[],
  storeId: number
): { ladder: T | null; source: PriceLadderSource } => {
  const attached = ladders.find(candidate => candidate.storeIds.includes(storeId));
  if (attached) return { ladder: attached, source: 'store_tier' };

  const fallback = ladders.find(candidate => candidate.isDefault);
  if (fallback) return { ladder: fallback, source: 'default_tier' };

  return { ladder: null, source: 'none' };
};

/* ------------------------------------------------------------------ */
/* Validation                                                          */
/* ------------------------------------------------------------------ */

/**
 * `null` when the ladder is legal; otherwise the Hebrew reason, ready to render as-is — the
 * dialog's helper text and the API's 400 message are the same string, the same arrangement
 * `priceTiersViolation` uses for עלונים.
 *
 * The monotonicity rule is the one with teeth, and it runs in **opposite directions** for the two
 * modes: an absolute ladder must fall (a rung above the one below it would quietly overcharge the
 * bigger order), a percent ladder must rise (5% at 10 units and 3% at 20 punishes buying more).
 * Both are the same statement — the more you buy, the less you pay per unit.
 */
export const priceLadderViolation = (basePrice: number | null, ladder: PriceLadder): string | null => {
  const rungs = ladder.tiers;

  if (rungs.length > MAX_PRICE_LADDER_RUNGS) return `ניתן להגדיר עד ${MAX_PRICE_LADDER_RUNGS} מדרגות כמות`;
  if (!PRICE_LADDER_MODES.includes(ladder.mode)) return 'סוג דרגת מחיר לא חוקי';
  if (!PRICE_LADDER_METHODS.includes(ladder.method)) return 'שיטת מדרוג לא חוקית';

  const sorted = [...rungs].sort((a, b) => a.minQuantity - b.minQuantity);

  // Absolute rungs start from the base price and must not climb above it. Percent rungs start
  // from "no discount", which is what 0 means.
  let previousPrice = basePrice ?? Number.POSITIVE_INFINITY;
  let previousPercent = 0;

  for (const [index, rung] of sorted.entries()) {
    if (!Number.isInteger(rung.minQuantity) || rung.minQuantity < 2) {
      return 'כמות המדרגה חייבת להיות מספר שלם, 2 ומעלה';
    }
    if (index > 0 && rung.minQuantity === sorted[index - 1].minQuantity) {
      return `הוגדרו שתי מדרגות לאותה כמות (${rung.minQuantity})`;
    }

    if (ladder.mode === 'unit_price') {
      if (!Number.isFinite(rung.unitPrice ?? NaN) || (rung.unitPrice as number) < 0) {
        return 'מחיר המדרגה חייב להיות מספר חיובי';
      }
      if ((rung.unitPrice as number) > previousPrice) {
        return `מחיר המדרגה ${rung.minQuantity} גבוה מהמחיר שלפניה — הנחת כמות חייבת להוזיל`;
      }
      previousPrice = rung.unitPrice as number;
    } else {
      const percent = rung.percentOff ?? NaN;
      if (!Number.isFinite(percent) || percent <= 0 || percent > 100) {
        return 'אחוז ההנחה חייב להיות בין 0 ל-100';
      }
      if (percent < previousPercent) {
        return `אחוז ההנחה במדרגה ${rung.minQuantity} נמוך מזה שלפניה — הנחת כמות חייבת להוזיל`;
      }
      previousPercent = percent;
    }
  }

  return null;
};

/* ------------------------------------------------------------------ */
/* Display                                                             */
/* ------------------------------------------------------------------ */

/** The ladder as one line: `10+ → 90 · 100+ → 80`, or `10+ → 5% · 20+ → 10%`. Empty when there is none. */
export const priceLadderSummary = (ladder: PriceLadder | null | undefined): string => {
  if (!ladder?.tiers.length) return '';

  return [...ladder.tiers]
    .sort((a, b) => a.minQuantity - b.minQuantity)
    .map(rung => `${rung.minQuantity}+ → ${ladder.mode === 'percent' ? `${rung.percentOff}%` : rung.unitPrice}`)
    .join(' · ');
};

/**
 * How the pricelist's "דרגות מחיר" column reports a variant.
 *
 * `multiple` is its own state rather than a count because the column answers a yes/no/it-depends
 * question: two ladders on one variant means different stores are priced differently, and that is
 * worth a different badge from "there is a ladder, here it is".
 */
export const PRICE_LADDER_STATES = ['none', 'single', 'multiple'] as const;
export type PriceLadderState = (typeof PRICE_LADDER_STATES)[number];

export const PRICE_LADDER_STATE_LABELS: Record<PriceLadderState, string> = {
  none: 'לא קיים',
  single: 'קיים',
  multiple: 'קיים מספר סוגי דרגות'
};

export const PRICE_LADDER_STATE_OPTIONS: PhOption<PriceLadderState>[] = phOptions(
  PRICE_LADDER_STATES,
  PRICE_LADDER_STATE_LABELS
);

export const priceLadderState = (count: number): PriceLadderState =>
  count === 0 ? 'none' : count === 1 ? 'single' : 'multiple';
